/**
 * Working document state from slice.
 * List only: listLoading, listError, list (from fetchWorkingDocuments thunk).
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import { useLogin } from "./useLogin";
import {
  fetchWorkingDocuments,
  requestRefreshWorkingDocuments,
} from "../slices/workingDocument/workingDocumentSlice";

export function useWorkingDocument() {
  const dispatch = useAppDispatch();
  const { teamsContext } = useLogin();
  const workingState = useAppSelector((state) => state.WorkingDocumnetStore);

  const isListLoading = workingState.listLoading === true;
  const isListError = workingState.listError != null;
  const list = workingState.workingDocumentList ?? [];

  const requestRefreshWorkingDocumentsAction = useCallback(
    () => dispatch(requestRefreshWorkingDocuments()),
    [dispatch],
  );

  const refreshWorkingDocuments = useCallback(
    (displayCount = 20) => {
      const tenantId = teamsContext?.user?.tenant?.id;
      if (!tenantId) return Promise.resolve();

      const channelId = teamsContext?.channel?.id ?? "";
      const isChannelContext = !!channelId;
      const groupId = isChannelContext
        ? teamsContext.channel.membershipType === "Private"
          ? teamsContext.channel.ownerGroupId
          : teamsContext?.team?.groupId ?? ""
        : "";

      return dispatch(
        fetchWorkingDocuments({
          teamId: groupId,
          channelId,
          tenantId,
          fileRoot: isChannelContext ? "/ChannelDoc" : "/MainMenu",
          displayCount,
          recFrom: 0,
          recSize: displayCount,
          append: false,
          searchText: "",
          fileType: "''",
        }),
      );
    },
    [
      dispatch,
      teamsContext?.team,
      teamsContext?.channel,
      teamsContext?.user?.tenant?.id,
    ]
  );

  const onWorkingDocumentLoad = useCallback(
    (teamId, channelId, tenantId, fileRoot, displayCount = 20, options = {}) =>
      dispatch(
        fetchWorkingDocuments({
          teamId,
          channelId,
          tenantId,
          fileRoot,
          displayCount,
          recFrom: options.recFrom ?? 0,
          recSize: options.recSize ?? displayCount,
          append: options.append === true,
          searchText: options.searchText ?? "",
          fileType: options.fileType ?? "''",
        })
      ),
    [dispatch]
  );

  const loadMoreWorkingDocuments = useCallback(
    (batchSize = 20) => {
      const tenantId = teamsContext?.user?.tenant?.id;
      if (!tenantId) return Promise.resolve(null);
      const channelId = teamsContext?.channel?.id ?? "";
      const isChannelContext = !!channelId;
      const groupId = isChannelContext
        ? teamsContext.channel.membershipType === "Private"
          ? teamsContext.channel.ownerGroupId
          : teamsContext?.team?.groupId ?? ""
        : "";
      const currentCount = Array.isArray(workingState?.workingDocumentList)
        ? workingState.workingDocumentList.length
        : 0;
      const q = workingState?.listQuery ?? {
        searchText: "",
        fileType: "''",
      };
      return dispatch(
        fetchWorkingDocuments({
          teamId: groupId,
          channelId,
          tenantId,
          fileRoot: isChannelContext ? "/ChannelDoc" : "/MainMenu",
          // Keep incremental loads constant-size; backend paging should use recFrom/recSize.
          displayCount: batchSize,
          recFrom: currentCount,
          recSize: batchSize,
          append: true,
          searchText: q.searchText ?? "",
          fileType: q.fileType ?? "''",
        })
      );
    },
    [
      dispatch,
      teamsContext?.team,
      teamsContext?.channel,
      teamsContext?.user?.tenant?.id,
      workingState?.workingDocumentList,
      workingState?.listQuery?.searchText,
      workingState?.listQuery?.fileType,
    ]
  );

  return {
    ...workingState,
    isListLoading,
    isListError,
    list,
    refreshTrigger: workingState.refreshTrigger ?? 0,
    requestRefreshWorkingDocuments: requestRefreshWorkingDocumentsAction,
    refreshWorkingDocuments,
    onWorkingDocumentLoad,
    loadMoreWorkingDocuments,
  };
}
